import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ReactNode } from 'react'
import { HiOutlineBars3, HiOutlineChevronDown, HiOutlineChevronUp } from 'react-icons/hi2'

// ────────────────────────────────────────────────────────────────
// SortableList — thin wrapper around dnd-kit's vertical sortable. The
// parent owns the data; we just emit a new ordered array on drop.
//
// Each row gets a drag-handle rendered by the caller (pass it via
// `renderItem({ dragHandle })`). This lets us drag from a specific
// icon/area instead of the whole row — important when rows contain
// inputs that need text-selection.
//
// We also expose up/down button helpers so callers can render arrow
// controls for rows that are too tall to drag comfortably (long
// question text, many options). Both paths emit the same arrayMove
// so state stays consistent.
// ────────────────────────────────────────────────────────────────

type Identifiable = { id: string }

export type SortableHelpers = {
  dragHandle: ReactNode
  moveControls: ReactNode
  isFirst: boolean
  isLast: boolean
  moveUp: () => void
  moveDown: () => void
}

type Size = 'xs' | 'sm' | 'md'

type Layout = 'list' | 'grid'

type Props<T extends Identifiable> = {
  items: T[]
  onReorder: (next: T[]) => void
  size?: Size
  layout?: Layout
  gridClassName?: string
  renderItem: (item: T, helpers: SortableHelpers) => ReactNode
}

export default function SortableList<T extends Identifiable>({ items, onReorder, size = 'md', layout = 'list', gridClassName, renderItem }: Props<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const from = items.findIndex((i) => i.id === active.id)
    const to = items.findIndex((i) => i.id === over.id)
    if (from === -1 || to === -1) return
    onReorder(arrayMove(items, from, to))
  }

  const moveBy = (index: number, delta: number) => {
    const target = index + delta
    if (target < 0 || target >= items.length) return
    onReorder(arrayMove(items, index, target))
  }

  const strategy = layout === 'grid' ? rectSortingStrategy : verticalListSortingStrategy
  const containerClass = layout === 'grid'
    ? (gridClassName || 'grid grid-cols-2 gap-3 md:grid-cols-3')
    : 'flex flex-col gap-3'

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={strategy}>
        <div className={containerClass}>
          {items.map((item, index) => (
            <SortableRow
              key={item.id}
              id={item.id}
              index={index}
              total={items.length}
              size={size}
              onMoveUp={() => moveBy(index, -1)}
              onMoveDown={() => moveBy(index, 1)}
            >
              {(helpers) => renderItem(item, helpers)}
            </SortableRow>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

function SortableRow({
  id,
  index,
  total,
  size,
  onMoveUp,
  onMoveDown,
  children,
}: {
  id: string
  index: number
  total: number
  size: Size
  onMoveUp: () => void
  onMoveDown: () => void
  children: (helpers: SortableHelpers) => ReactNode
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  }
  const isFirst = index === 0
  const isLast = index === total - 1

  const dragHandle = (
    <button
      type="button"
      {...attributes}
      {...listeners}
      className="flex h-8 w-8 shrink-0 cursor-grab items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 active:cursor-grabbing"
      aria-label="Drag to reorder"
    >
      <HiOutlineBars3 className="h-5 w-5" />
    </button>
  )

  const sizeDims =
    size === 'xs'
      ? 'h-4 w-5'
      : size === 'sm'
        ? 'h-5 w-6'
        : 'h-7 w-7'
  const btnClass = `flex ${sizeDims} shrink-0 items-center justify-center text-gray-600 transition hover:bg-gray-100 hover:text-ink disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-300 disabled:hover:bg-gray-50`
  const iconClass = size === 'md' ? 'h-4 w-4' : 'h-3 w-3'

  const moveControls = (
    <div className="inline-flex flex-col overflow-hidden rounded-md border border-gray-300 bg-white shadow-sm">
      <button
        type="button"
        onClick={onMoveUp}
        disabled={isFirst}
        className={`${btnClass} border-b border-gray-300`}
        aria-label="Move up"
      >
        <HiOutlineChevronUp className={iconClass} strokeWidth={2.5} />
      </button>
      <button
        type="button"
        onClick={onMoveDown}
        disabled={isLast}
        className={btnClass}
        aria-label="Move down"
      >
        <HiOutlineChevronDown className={iconClass} strokeWidth={2.5} />
      </button>
    </div>
  )

  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragHandle, moveControls, isFirst, isLast, moveUp: onMoveUp, moveDown: onMoveDown })}
    </div>
  )
}
